'use client';
import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from '@/lib/toast';
import { withRetry, formatAuthErrorMessage } from '@/lib/client/authRetryHelper';
import {
  Eye,
  EyeOff,
  TrendingUp,
  Shield,
  Zap,
  BarChart3,
  CheckCircle2,
  Loader2,
  Globe,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient, setAuthPersistenceMode } from '@/lib/supabase/client';
import { useLanguage, Language } from '@/context/LanguageContext';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import Link from 'next/link';
import { buildDeviceDescriptor, clearDeviceFlowState, ensureDeviceId } from '@/lib/deviceTrust';
import { collectDeviceLocationContext } from '@/lib/deviceLocation';
import { expireBrowserCookie, isLikelyAuthCookieName } from '@/lib/authCookieCleanup';

type LoginForm = { email: string; password: string; remember: boolean };
type SignupForm = {
  name: string;
  email: string;
  password: string;
  confirm: string;
  terms: boolean;
};

const DEMO_EMAIL = 'teste@greenpro.com';
const DEMO_PASSWORD = 'GreenPro00!';

const SIGNUP_COOLDOWN_KEY = 'gp_signup_cooldown_until';

const LANGUAGES: { code: Language; label: string; flag: string }[] = [
  { code: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const features = [
  { icon: TrendingUp, labelKey: 'login.bankrollTracking', descKey: 'login.bankrollTrackingDesc' },
  { icon: BarChart3, labelKey: 'login.advancedAnalytics', descKey: 'login.advancedAnalyticsDesc' },
  { icon: Shuffle2, labelKey: 'login.surebetManager', descKey: 'login.surebetManagerDesc' },
  { icon: Shield, labelKey: 'login.dataIsolation', descKey: 'login.dataIsolationDesc' },
];

function Shuffle2({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const current = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.12] text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-all duration-200"
      >
        <Globe size={13} className="text-[hsl(var(--muted-foreground))]" />
        <span>{current.flag}</span>
        <span className="text-[hsl(var(--muted-foreground))]">{current.label}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-[#1a1a2e] border border-white/[0.12] rounded-lg shadow-xl z-50 min-w-[140px] overflow-hidden">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => {
                setLanguage(lang.code);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors hover:bg-white/[0.06] ${
                language === lang.code ? 'text-brand-400 bg-brand-900/20' : 'text-white'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SignUpLoginInner() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [loginAutofillEnabled, setLoginAutofillEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loginCooldownSeconds, setLoginCooldownSeconds] = useState(0);
  const [signupCooldownSeconds, setSignupCooldownSeconds] = useState(0);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();
  const { t } = useLanguage();
  const autoCleanupRanRef = useRef(false);

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', remember: true },
  });
  const signupForm = useForm<SignupForm>({
    defaultValues: { name: '', email: '', password: '', confirm: '', terms: false },
  });

  const isRateLimitError = (err: any): boolean => {
    const msg = err?.message?.toLowerCase() || '';
    return (
      msg.includes('rate limit') ||
      msg.includes('too many requests') ||
      msg.includes('over_email_send_rate_limit') ||
      err?.status === 429
    );
  };

  const isSessionArtifactError = (err: any): boolean => {
    const msg = (err?.message || '').toLowerCase();
    const code = (err?.code || '').toLowerCase();
    return (
      msg.includes('refresh token') ||
      msg.includes('refresh_token') ||
      msg.includes('refresh_token_not_found') ||
      msg.includes('invalid grant') ||
      msg.includes('invalid session') ||
      msg.includes('code verifier') ||
      code.includes('refresh_token_not_found') ||
      code.includes('invalid_grant')
    );
  };

  const isEmailNotConfirmedError = (err: any): boolean => {
    const msg = (err?.message || '').toLowerCase();
    const code = (err?.code || '').toLowerCase();
    return (
      code === 'email_not_confirmed' ||
      msg.includes('email not confirmed') ||
      msg.includes('not confirmed')
    );
  };

  useEffect(() => {
    const requestedMode = searchParams.get('mode');
    if (requestedMode === 'signup') {
      setMode('signup');
    }
  }, [searchParams]);

  const clearLocalSessionData = () => {
    try {
      if (typeof window !== 'undefined') {
        // Remove auth-related keys from local/session storage.
        const localKeys = Object.keys(window.localStorage);
        for (const key of localKeys) {
          if (
            key.startsWith('sb-') ||
            key.startsWith('sb_') ||
            key.includes('supabase') ||
            key.toLowerCase().includes('auth')
          ) {
            window.localStorage.removeItem(key);
          }
        }

        const sessionKeys = Object.keys(window.sessionStorage);
        for (const key of sessionKeys) {
          if (
            key.startsWith('sb-') ||
            key.startsWith('sb_') ||
            key.includes('supabase') ||
            key.toLowerCase().includes('auth')
          ) {
            window.sessionStorage.removeItem(key);
          }
        }

        if (document.cookie) {
          const cookieNames = document.cookie
            .split(';')
            .map((cookie) => cookie.split('=')[0].trim());
          for (const name of cookieNames) {
            if (!name) continue;
            const isAuthCookie = isLikelyAuthCookieName(name);

            if (!isAuthCookie) continue;

            expireBrowserCookie(name);
          }
        }
      }
    } catch {
      // Silent cleanup failure to avoid interrupting auth flow.
    }

    clearDeviceFlowState({ preserveDeviceId: true });
  };

  const requestServerSessionReset = async () => {
    try {
      await fetch('/api/auth/reset-session', {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
      });
    } catch {
      // Keep client-side recovery usable even if server cleanup fails.
    }
  };

  const clearLocalSessionWithFeedback = async () => {
    toast.info(t('login.toasts.clearingSessionTitle'), {
      description: t('login.toasts.clearingSessionDescription'),
    });
    clearLocalSessionData();
    await requestServerSessionReset();
    toast.success(t('login.localSessionClearedTitle') || 'Sessao local limpa com sucesso.', {
      description:
        t('login.localSessionClearedDescription') ||
        'Cookies e armazenamento local foram removidos.',
    });
  };

  const resetLocalAuthArtifacts = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch {
      // Ignore sign-out failures and proceed with local cleanup.
    }

    clearLocalSessionData();
    await requestServerSessionReset();
  };

  const parseRetryAfterSeconds = (response: Response, body: any): number => {
    const fromBody = Number(body?.retryAfter);
    if (Number.isFinite(fromBody) && fromBody > 0) return Math.ceil(fromBody);

    const header = response.headers.get('Retry-After');
    const fromHeader = Number(header);
    if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);

    return 60;
  };

  useEffect(() => {
    if (autoCleanupRanRef.current) return;
    autoCleanupRanRef.current = true;

    // Evita chamada extra ao Auth no mount da tela de login.
    return;
  }, [supabase]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = localStorage.getItem(SIGNUP_COOLDOWN_KEY);
    if (!raw) return;
    try {
      const until = Number(JSON.parse(raw));
      const remaining = Math.ceil((until - Date.now()) / 1000);
      if (remaining > 0) setSignupCooldownSeconds(remaining);
      else localStorage.removeItem(SIGNUP_COOLDOWN_KEY);
    } catch {
      localStorage.removeItem(SIGNUP_COOLDOWN_KEY);
    }
  }, []);

  useEffect(() => {
    if (loginCooldownSeconds <= 0) return;

    const intervalId = window.setInterval(() => {
      setLoginCooldownSeconds((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [loginCooldownSeconds]);

  useEffect(() => {
    if (signupCooldownSeconds <= 0) return;

    const intervalId = window.setInterval(() => {
      setSignupCooldownSeconds((current) => {
        if (current <= 1) {
          window.clearInterval(intervalId);
          if (typeof window !== 'undefined') localStorage.removeItem(SIGNUP_COOLDOWN_KEY);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [signupCooldownSeconds]);

  const handleGoogleLogin = async () => {
    if (googleLoading) return;

    if (mode === 'signup' && !signupForm.getValues('terms')) {
      signupForm.setError('terms', { type: 'required', message: t('login.termsRequired') });
      toast.error(t('login.termsRequired'));
      return;
    }

    let googleCaptchaToken = '';
    if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
      if (!executeRecaptcha) {
        toast.error(t('login.toasts.captchaNotLoaded'));
        return;
      }
      googleCaptchaToken = await executeRecaptcha('google_login');
    }

    toast.info(t('login.toasts.googleStartTitle'), {
      description: t('login.toasts.googleStartDescription'),
    });

    setGoogleLoading(true);
    const locationContextPromise = collectDeviceLocationContext();

    await resetLocalAuthArtifacts();

    const performGoogleAuth = async () => {
      setAuthPersistenceMode(Boolean(loginForm.getValues('remember')));
      const deviceId = ensureDeviceId();
      const deviceDescriptor = buildDeviceDescriptor();
      const locationContext = await locationContextPromise;

      const res = await fetch('/api/auth/google/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          captchaToken: googleCaptchaToken,
          mode,
          acceptedTerms: mode === 'signup' ? Boolean(signupForm.getValues('terms')) : false,
          deviceId,
          deviceName: deviceDescriptor.deviceName,
          browserName: deviceDescriptor.browserName,
          platformName: deviceDescriptor.platformName,
          timeZone: deviceDescriptor.timeZone,
          browserFingerprint: deviceDescriptor.browserFingerprint,
          locationHint: locationContext.locationHint,
          clientLocation: locationContext.clientLocation,
        }),
      });

      const body = await res.json().catch(() => ({}));

      if (!res.ok || !body?.url) {
        const error = new Error(body?.error || 'Falha ao iniciar autenticação com Google.');
        (error as any).status = res.status;
        (error as any).code = body?.code;
        (error as any).retryAfter = parseRetryAfterSeconds(res, body);
        throw error;
      }

      return body;
    };

    try {
      // Tenta com retry automático
      const result = await withRetry(performGoogleAuth, { maxAttempts: 2 });
      window.location.assign(result.url);
    } catch (err: any) {
      if (isSessionArtifactError(err)) {
        clearLocalSessionData();
      }

      // Usa novo formatador de erro melhorado
      const errorInfo = formatAuthErrorMessage(err);
      toast.error(errorInfo.title, {
        description: errorInfo.description,
      });

      // ⚠️ Importante: resetar o loading apenas se houver erro,
      // pois se der certo, a página será redirecionada.
      setGoogleLoading(false);
    }
  };

  const isSubmitting = useRef(false);

  const handleLogin = async (data: LoginForm) => {
    if (isSubmitting.current || loading || loginCooldownSeconds > 0) return;

    isSubmitting.current = true;
    setLoading(true);

    let loginCaptchaToken = '';
    if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
      if (!executeRecaptcha) {
        toast.error(t('login.toasts.captchaNotLoaded'));
        setLoading(false);
        isSubmitting.current = false;
        return;
      }
      loginCaptchaToken = await executeRecaptcha('login');
    }

    const locationContextPromise = collectDeviceLocationContext();
    await resetLocalAuthArtifacts();

    let attemptCount = 0;
    const deviceId = ensureDeviceId();
    const deviceDescriptor = buildDeviceDescriptor();
    const performLogin = async () => {
      attemptCount++;

      setAuthPersistenceMode(Boolean(data.remember));
      const locationContext = await locationContextPromise;

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          captchaToken: loginCaptchaToken,
          deviceId,
          deviceName: deviceDescriptor.deviceName,
          browserName: deviceDescriptor.browserName,
          platformName: deviceDescriptor.platformName,
          timeZone: deviceDescriptor.timeZone,
          browserFingerprint: deviceDescriptor.browserFingerprint,
          locationHint: locationContext.locationHint,
          clientLocation: locationContext.clientLocation,
        }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        const err = new Error(body?.error || t('login.toasts.loginErrorFallback'));
        (err as any).status = response.status;
        (err as any).code = body?.code;
        (err as any).retryAfter = parseRetryAfterSeconds(response, body);
        throw err;
      }

      return body;
    };

    try {
      if (attemptCount === 0) {
        toast.info(t('login.toasts.validatingTitle'), {
          description: t('login.toasts.validatingDescription'),
        });
      }

      // Tenta com retry automático
      const loginResult = await withRetry(performLogin, { maxAttempts: 3 });

      // Ensure the browser cookie is in sync before the next protected navigation.
      ensureDeviceId();

      const fullName =
        (loginResult?.user?.fullName as string | undefined) ||
        (loginResult?.user?.name as string | undefined) ||
        '';
      const firstNameFromMetadata = fullName.trim().split(' ')[0] || '';
      const firstNameFromEmail = (loginResult?.user?.email || data.email).split('@')[0] || '';
      const firstName = firstNameFromMetadata || firstNameFromEmail || 'usuário';

      toast.success(t('login.toasts.welcomeBack').replace('{name}', firstName), {
        description: t('login.toasts.redirectingDashboard'),
      });
      setLoginCooldownSeconds(0);
      window.location.assign(loginResult?.next || '/dashboard');
    } catch (err: any) {
      if (isSessionArtifactError(err)) {
        clearLocalSessionData();
      }

      if (isEmailNotConfirmedError(err)) {
        window.location.assign('/confirm-email');
        return;
      }

      if (err?.status === 429) {
        setLoginCooldownSeconds(Number(err?.retryAfter) || 0);
      }

      // Usa novo formatador de erro melhorado
      const errorInfo = formatAuthErrorMessage(err);
      toast.error(errorInfo.title, {
        description: errorInfo.description,
      });
    } finally {
      setLoading(false);
      isSubmitting.current = false;
    }
  };

  // Corrigido para "handleSignup" (minúsculo) para bater com o onSubmit do formulário
  const isSigningUp = useRef(false);

  const handleSignup = async (data: SignupForm) => {
    if (isSigningUp.current || loading || signupCooldownSeconds > 0) return;

    isSigningUp.current = true;
    setLoading(true);

    let signupCaptchaToken = '';
    if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
      if (!executeRecaptcha) {
        toast.error(t('login.toasts.captchaNotLoaded'));
        setLoading(false);
        isSigningUp.current = false;
        return;
      }
      signupCaptchaToken = await executeRecaptcha('signup').catch(() => '');
    }

    toast.info(t('login.toasts.creatingAccountTitle'), {
      description: t('login.toasts.creatingAccountDescription'),
    });

    try {
      const deviceId = ensureDeviceId();
      const deviceDescriptor = buildDeviceDescriptor();
      const locationContext = await collectDeviceLocationContext();

      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          password: data.password,
          acceptedTerms: data.terms,
          captchaToken: signupCaptchaToken,
          deviceId,
          deviceName: deviceDescriptor.deviceName,
          browserName: deviceDescriptor.browserName,
          platformName: deviceDescriptor.platformName,
          timeZone: deviceDescriptor.timeZone,
          browserFingerprint: deviceDescriptor.browserFingerprint,
          locationHint: locationContext.locationHint,
          clientLocation: locationContext.clientLocation,
        }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        const err = new Error(body?.error || t('login.toasts.signupErrorFallback'));
        (err as any).status = response.status;
        (err as any).code = body?.code;
        (err as any).retryAfter = parseRetryAfterSeconds(response, body);
        throw err;
      }

      const redirectSeconds = 5;
      toast.success(t('login.toasts.signupSuccessTitle'), {
        description: t('login.toasts.signupSuccessDescription'),
      });
      toast.info(t('login.toasts.confirmEmailRedirectTitle'), {
        description: t('login.toasts.confirmEmailRedirectDescription').replace(
          '{seconds}',
          String(redirectSeconds)
        ),
      });

      signupForm.reset();
      window.setTimeout(() => {
        window.location.assign('/confirm-email');
      }, redirectSeconds * 1000);
    } catch (err: any) {
      if (err?.status === 429) {
        const retryAfter = Number(err?.retryAfter) || 3600;
        setSignupCooldownSeconds(retryAfter);
        if (typeof window !== 'undefined') {
          localStorage.setItem(SIGNUP_COOLDOWN_KEY, JSON.stringify(Date.now() + retryAfter * 1000));
        }
      }

      const errorInfo = formatAuthErrorMessage(err);
      toast.error(errorInfo.title, { description: errorInfo.description });
    } finally {
      setLoading(false);
      isSigningUp.current = false;
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0a0f]">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] relative flex-col justify-between p-10 xl:p-14 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d0d14] via-[#0f0b1a] to-[#0a0a0f]" />
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-brand-700/10 rounded-full blur-[120px] -translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-brand-900/20 rounded-full blur-[100px] translate-x-1/4 translate-y-1/4" />

        <div className="relative z-10">
          <div className="flex items-center gap-">
            <AppLogo size={250} priority />
            {/*<span className="text-xl font-bold text-gradient-green">GreenPro</span>*/}
          </div>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-lg">
          <div className="inline-flex items-center gap-2 bg-brand-700/20 border border-brand-700/30 rounded-full px-3 py-1 mb-6 w-fit">
            <Zap size={12} className="text-brand-400" />
            <span className="text-xs font-medium text-brand-300">
              {t('login.professionalBettingAnalytics')}
            </span>
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            {t('login.tagline')
              .split('\n')
              .map((line, i) => (
                <React.Fragment key={i}>
                  {i === 0 ? (
                    line
                  ) : (
                    <>
                      <br />
                      <span className="text-gradient-green">{line}</span>
                    </>
                  )}
                </React.Fragment>
              ))}
          </h1>

          <p className="text-[hsl(var(--muted-foreground))] text-base leading-relaxed mb-10">
            {t('login.taglineDesc')}
          </p>

          <div className="grid grid-cols-2 gap-3">
            {features.map((f) => (
              <div
                key={`feature-${f.labelKey}`}
                className="flex items-start gap-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-3.5"
              >
                <div className="w-8 h-8 rounded-lg bg-brand-700/30 flex items-center justify-center flex-shrink-0">
                  <f.icon size={15} className="text-brand-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{t(f.labelKey)}</p>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">
                    {t(f.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-6 mt-2">
          {[
            { value: 'R$4.7M+', labelKey: 'login.bankrollTracked' },
            { value: '125K+', labelKey: 'login.betsLogged' },
            { value: '99,9%', labelKey: 'login.uptimeSla' },
          ].map((stat) => (
            <div key={`stat-${stat.labelKey}`}>
              <p className="text-xl font-bold text-white font-mono">{stat.value}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{t(stat.labelKey)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-8 py-10 relative">
        <div className="absolute inset-0 bg-[#0d0d14]" />

        {/* Language selector — top right */}
        <div className="absolute top-4 right-4 z-20">
          <LanguageSelector />
        </div>

        <div className="relative z-10 w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-1 lg:hidden">
            <AppLogo size={100} />
            {/*<span className="text-lg font-bold text-gradient-green">GreenPro</span>*/}
          </div>

          {/* Tab switcher */}
          <div className="flex bg-[hsl(var(--muted))] rounded-xl p-1 mb-6">
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={`tab-${m}`}
                onClick={() => {
                  setMode(m);
                  if (m === 'login') {
                    setLoginAutofillEnabled(false);
                  }
                }}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  mode === m
                    ? 'bg-brand-700 text-white shadow-lg shadow-brand-900/30'
                    : 'text-[hsl(var(--muted-foreground))] hover:text-white'
                }`}
              >
                {m === 'login' ? t('login.signIn') : t('login.createAccount')}
              </button>
            ))}
          </div>

          {/* Google OAuth Button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.12] text-white font-medium py-2 rounded-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mb-4"
          >
            {googleLoading ? <Loader2 size={16} className="animate-spin" /> : <GoogleIcon />}
            <span className="text-sm">
              {mode === 'login'
                ? t('login.signInWithGoogle') || 'Entrar com Google'
                : t('login.signUpWithGoogle') || 'Cadastrar com Google'}
            </span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-[hsl(var(--border))]" />
            <span className="text-xs text-[hsl(var(--muted-foreground))]">
              {t('login.orContinueWithEmail') || 'ou continue com email'}
            </span>
            <div className="flex-1 h-px bg-[hsl(var(--border))]" />
          </div>

          {mode === 'login' ? (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-white mb-1">{t('login.welcomeBack')}</h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                {t('login.signInSubtitle')}
              </p>

              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">
                    {t('login.email')}
                  </label>
                  <input
                    type="email"
                    autoComplete="username"
                    readOnly={!loginAutofillEnabled}
                    onFocus={() => setLoginAutofillEnabled(true)}
                    {...loginForm.register('email', {
                      required: 'Email é obrigatório',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                    })}
                    className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-lg px-4 py-2 text-sm text-white placeholder-[hsl(var(--muted-foreground))] outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition-all"
                    placeholder="voce@exemplo.com"
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-red-400 text-xs mt-1">
                      {loginForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-white">
                      {t('login.password')}
                    </label>
                    <Link href="/forgot-password">
                      <button
                        type="button"
                        className="text-xs text-brand-400 hover:text-brand-300 transition-colors"
                      >
                        {t('login.forgotPassword')}
                      </button>
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      readOnly={!loginAutofillEnabled}
                      onFocus={() => setLoginAutofillEnabled(true)}
                      {...loginForm.register('password', {
                        required: 'Senha é obrigatória',
                        minLength: { value: 6, message: 'Mínimo 6 caracteres' },
                      })}
                      className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-lg px-4 py-2 text-sm text-white placeholder-[hsl(var(--muted-foreground))] outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition-all pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-red-400 text-xs mt-1">
                      {loginForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5">
                    <input
                      id="remember"
                      type="checkbox"
                      {...loginForm.register('remember')}
                      className="h-3.5 w-3.5 rounded border-[hsl(var(--border))] bg-[hsl(var(--input))] accent-brand-500 cursor-pointer"
                    />
                    <label
                      htmlFor="remember"
                      className="text-[12px] text-[hsl(var(--muted-foreground))] cursor-pointer"
                    >
                      {t('login.rememberMe')}
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={clearLocalSessionWithFeedback}
                    className="group inline-flex items-center gap-1.5 rounded-md border border-white/12 bg-white/[0.02] px-2 py-0.5 text-[10px] font-medium tracking-[0.01em] text-white/70 transition-all duration-200 hover:border-green-500/35 hover:bg-green-500/[0.07] hover:text-green-200"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500/60 shadow-[0_0_0_3px_rgba(34,197,94,0.12)] transition-colors duration-200 group-hover:bg-green-400/80" />
                    {t('login.clearLocalSession') || 'Problemas ao fazer login? Clique aqui'}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading || loginCooldownSeconds > 0}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-500 hover:to-brand-600 text-white font-semibold py-2 rounded-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-brand-900/30"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> {t('login.signingIn')}
                    </>
                  ) : loginCooldownSeconds > 0 ? (
                    <>{`${t('login.signIn')} (${loginCooldownSeconds}s)`}</>
                  ) : (
                    <>{t('login.signIn')}</>
                  )}
                </button>
              </form>

              <div className="mt-3 bg-[#0a0a0a] border border-zinc-800 rounded-2xl p-4 shadow-[0_0_16px_rgba(34,197,94,0.05)] relative overflow-hidden group">
                {/* Efeito de brilho no fundo (Blur) */}
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-green-500/10 blur-[80px] rounded-full group-hover:bg-green-500/30 transition-all duration-700" />

                {/* Cabeçalho */}
                <div className="flex items-center gap-4 mb-3 relative z-10">
                  <div className="bg-green-500/10 p-2 rounded-xl border border-green-500/30 shadow-[0_0_12px_rgba(34,197,94,0.1)]">
                    <CheckCircle2 size={18} className="text-green-400" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[12px] text-zinc-500 font-medium uppercase tracking-wider">
                      {t('login.offerBadge') || 'Oferta Exclusiva'}
                    </span>
                    <h3 className="text-base font-black tracking-tight text-white">
                      <span className="text-green-400">Green</span>Pro{' '}
                      <span className="text-zinc-500 font-light mx-1">—</span>{' '}
                      <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                        {t('login.offerFree') || 'Gratis'}
                      </span>
                    </h3>
                  </div>
                </div>

                {/* Descrição */}
                <p className="text-[12px] text-zinc-400 leading-relaxed relative z-10">
                  <span>{t('login.offerStart') || 'Crie sua conta agora e garanta'}</span>{' '}
                  <span className="text-orange-500 font-extrabold px-1 py-0.5 rounded-md bg-orange-500/10 mx-1 border border-orange-500/20 shadow-[0_0_10px_rgba(249,115,22,0.1)]">
                    {t('login.offerDays') || '5 dias'}
                  </span>{' '}
                  <span>{t('login.offerAccess') || 'de acesso'}</span>{' '}
                  <span className="text-white font-bold underline decoration-green-500 decoration-2 underline-offset-4 ml-1">
                    {t('login.offerStarterPlan') || 'Starter'}
                  </span>
                  <span className="block mt-1.5 text-zinc-500 italic text-[12px]">
                    {t('login.offerSubtitle') ||
                      'Aproveite os recursos Starter. Monitor Pré-Live é exclusivo do PRO.'}
                  </span>
                </p>

                {/* Box inferior (Estilo Glassmorphism) */}
                <div className="mt-2 flex items-center justify-between bg-zinc-900/50 border border-zinc-800/50 rounded-xl px-3.5 py-1.5 backdrop-blur-sm relative z-10 transition-colors group-hover:border-green-500/30">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-[12px] font-medium text-zinc-300">
                      {t('login.offerInstant') || 'Acesso Instantaneo'}
                    </span>
                  </div>

                  <div className="text-right">
                    <span className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-0.2">
                      {t('login.offerCancellation') || 'Cancelamento'}
                    </span>
                    <span className="text-xs font-bold text-white tracking-wide">
                      {t('login.offerNoCommitment') || 'SEM COMPROMISSO'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Versão anterior do box de oferta (estilo mais sólido, sem glassmorphism)
<div className="mt-6 bg-gradient-to-br from-black/10 via-green-950/80 to-black/40 border border-green-700/50 rounded-xl p-6 shadow-lg">

  // Cabeçalho
  <div className="flex items-center gap-4 mb-4">
    <div className="bg-green-600/20 p-2 rounded-xl border-2 border-green-500/60">
      <CheckCircle2 size={18} className="text-green-500" />
    </div>
    <span className="text-lg font-extrabold text-white">
     🎉 <span className="text-green-500">Green</span><span className="text-yellow-400">Pro</span> - Grátis
    </span>
  </div>

  // Descrição
  <p className="text-[13.5px] text-gray-300 leading-relaxed">
    Crie sua conta e aproveite
    <span className="text-green-500 font-bold"> 5 dias</span> de acesso <span className="text-yellow-400 font-bold">Starter </span>
    para testar os recursos Starter. Monitor Pré-Live é exclusivo do PRO.
  </p>

  // Box inferior
  <div className="mt-5 flex items-center justify-between bg-green-950/40 border border-green-500/30 rounded-lg px-5 py-3 shadow-inner">
    <span className="text-sm text-gray-300">
      ✔ Acesso completo liberado
    </span>
    <span className="text-sm font-bold text-green-500">
      Sem compromisso
    </span>
  </div>

</div> */}

              {/*
                   // Demo credentials 
               <div className="mt-5 bg-brand-950/60 border border-brand-800/40 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 size={14} className="text-brand-400" />
                  <span className="text-xs font-semibold text-brand-300">{t('login.demoCredentials')}</span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Email</span>
                    <span className="text-[11px] font-mono text-brand-300">{DEMO_EMAIL}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{t('login.password')}</span>
                    <span className="text-[11px] font-mono text-brand-300">{DEMO_PASSWORD}</span>
                  </div>
                </div>
              </div> */}
            </div>
          ) : (
            <div className="animate-fade-in">
              <h2 className="text-2xl font-bold text-white mb-1">
                {t('login.createYourAccount') || 'Crie sua conta'}
              </h2>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">
                {t('login.createAccountSubtitle') ||
                  'Comece a gerenciar seu bankroll profissionalmente'}
              </p>

              <form
                onSubmit={signupForm.handleSubmit(handleSignup)}
                className="space-y-4"
                autoComplete="on"
              >
                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">
                    {t('login.fullName') || 'Nome completo'}
                  </label>
                  <input
                    type="text"
                    autoComplete="name"
                    {...signupForm.register('name', { required: 'Nome é obrigatório' })}
                    className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-lg px-4 py-2 text-sm text-white placeholder-[hsl(var(--muted-foreground))] outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition-all"
                    placeholder="Seu Nome Completo"
                  />
                  {signupForm.formState.errors.name && (
                    <p className="text-red-400 text-xs mt-1">
                      {signupForm.formState.errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">
                    {t('login.email')}
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    {...signupForm.register('email', {
                      required: 'Email é obrigatório',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                    })}
                    className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-lg px-4 py-2 text-sm text-white placeholder-[hsl(var(--muted-foreground))] outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition-all"
                    placeholder="voce@exemplo.com"
                  />
                  {signupForm.formState.errors.email && (
                    <p className="text-red-400 text-xs mt-1">
                      {signupForm.formState.errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">
                    {t('login.password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      {...signupForm.register('password', {
                        required: 'Senha é obrigatória',
                        minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                      })}
                      className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-lg px-4 py-2 text-sm text-white placeholder-[hsl(var(--muted-foreground))] outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition-all pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.password && (
                    <p className="text-red-400 text-xs mt-1">
                      {signupForm.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-1.5">
                    {t('login.confirmPassword') || 'Confirmar senha'}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      autoComplete="new-password"
                      {...signupForm.register('confirm', {
                        required: 'Por favor confirme sua senha',
                      })}
                      className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--border))] rounded-lg px-4 py-2 text-sm text-white placeholder-[hsl(var(--muted-foreground))] outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30 transition-all pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-white transition-colors"
                    >
                      {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {signupForm.formState.errors.confirm && (
                    <p className="text-red-400 text-xs mt-1">
                      {signupForm.formState.errors.confirm.message}
                    </p>
                  )}
                </div>

                <div className="flex items-start gap-2">
                  <input
                    id="terms"
                    type="checkbox"
                    {...signupForm.register('terms', {
                      required: t('login.termsRequired'),
                    })}
                    className="w-4 h-4 mt-0.5 rounded border-[hsl(var(--border))] bg-[hsl(var(--input))] accent-brand-500 cursor-pointer flex-shrink-0"
                  />
                  <label
                    htmlFor="terms"
                    className="text-xs text-[hsl(var(--muted-foreground))] cursor-pointer leading-relaxed"
                  >
                    {t('login.agreeWith') || 'Concordo com os'}{' '}
                    <Link
                      href="/termos-de-servico"
                      target="_blank"
                      className="text-brand-400 hover:text-brand-300 cursor-pointer underline-offset-2 hover:underline"
                    >
                      {t('login.termsOfService') || 'Termos de Serviço'}
                    </Link>{' '}
                    {t('login.and') || 'e'}{' '}
                    <Link
                      href="/politica-de-privacidade"
                      target="_blank"
                      className="text-brand-400 hover:text-brand-300 cursor-pointer underline-offset-2 hover:underline"
                    >
                      {t('login.privacyPolicy') || 'Política de Privacidade'}
                    </Link>{' '}
                    {t('login.ofBetVault') || 'do GreenPro'}
                  </label>
                </div>
                {signupForm.formState.errors.terms && (
                  <p className="-mt-2 text-xs text-red-400">
                    {String(signupForm.formState.errors.terms.message || '')}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || signupCooldownSeconds > 0}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-500 hover:to-brand-600 text-white font-semibold py-2 rounded-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-brand-900/30"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />{' '}
                      {t('login.creatingAccount') || 'Criando conta...'}
                    </>
                  ) : signupCooldownSeconds > 0 ? (
                    <>{`${t('login.createAccount')} (${signupCooldownSeconds}s)`}</>
                  ) : (
                    <>{t('login.createAccount')}</>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SignUpLoginClient() {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
  if (!siteKey) return <SignUpLoginInner />;
  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey} language="pt-BR">
      <SignUpLoginInner />
    </GoogleReCaptchaProvider>
  );
}
